import {
  Ban,
  CameraOff,
  Flag,
  HeartHandshake,
  KeyRound,
  LockKeyhole,
  LogOut,
  MessageSquareWarning,
  MicOff,
  ShieldCheck,
  Siren,
  UserRoundCheck,
  UsersRound,
} from "lucide-react";
import { Link } from "react-router-dom";

const safetyItems: Array<[typeof ShieldCheck, string, string]> = [
  [
    LogOut,
    "Leave immediately",
    "You never have to stay in a room. Leave first if a person, message, camera, or request makes you uncomfortable.",
  ],
  [
    MicOff,
    "Mute and stop media",
    "Mute audio, stop video, hide an avatar, or disable browser permission. Joining a room does not activate your camera or microphone.",
  ],
  [
    Ban,
    "Block unwanted contact",
    "Blocking and local mute change your experience immediately. Do not respond to threats, pressure, or requests for secrets.",
  ],
  [
    Flag,
    "Report with context",
    "Use the room report control or contact support. Include the room code, account, approximate time, and what happened.",
  ],
  [
    LockKeyhole,
    "Protect private rooms",
    "Treat a private link like an invitation. Reset or stop sharing it if it reaches someone you did not invite.",
  ],
  [
    UserRoundCheck,
    "Age-aware controls",
    "Accounts start at age 13. Players aged 13–17 require guardian review and receive stricter communication settings.",
  ],
];

const prohibitedConduct = [
  "Child sexual exploitation, grooming, sexualisation, or requests for a young person’s images, location, credentials, or off-platform contact.",
  "Credible threats, violent extremism, encouragement of self-harm, dangerous challenges, or instructions intended to cause real-world harm.",
  "Harassment, stalking, hate, blackmail, impersonation, doxxing, non-consensual intimate content, or unwanted sexual conduct.",
  "Fraud, phishing, malware, account theft, illegal gambling, or sales of illegal or dangerous goods and services.",
  "Cheating, collusion, bug exploitation, report abuse, ban evasion, spam, or bypassing privacy, age, moderation, and security controls.",
];

export function SafetyPage() {
  return (
    <article className="page-shell safety-page" id="top">
      <header className="page-intro safety-intro">
        <span className="eyebrow">TOPZLE SAFETY</span>
        <h1>Play together without giving up control.</h1>
        <p>
          Practical controls, clear boundaries, and age-aware communication help make social game
          rooms safer. No control replaces your judgment—leave and get help whenever something feels
          wrong.
        </p>
        <div className="safety-urgent-note">
          <Siren />
          <span>
            <strong>Immediate danger or a medical emergency?</strong>
            <small>
              Leave Topzle and contact local emergency services or a trusted adult. Topzle support
              is not an emergency service.
            </small>
          </span>
        </div>
      </header>

      <nav className="safety-page-nav" aria-label="Safety page contents">
        <a href="#quick-actions">Quick actions</a>
        <a href="#community-boundaries">Community boundaries</a>
        <a href="#rooms-calls">Rooms and calls</a>
        <a href="#young-players">Young players</a>
        <a href="#reporting">Reporting and review</a>
        <a href="#account-safety">Scams and account safety</a>
        <a href="#families">Families</a>
      </nav>

      <section id="quick-actions" className="safety-section">
        <span className="eyebrow">ACT FIRST</span>
        <h2>What to do when something feels wrong</h2>
        <div className="safety-grid">
          {safetyItems.map(([Component, title, copy]) => (
            <article className="safety-card" key={title}>
              <Component />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="safety-policy-layout">
        <section id="community-boundaries" className="safety-policy-card">
          <MessageSquareWarning />
          <div>
            <span className="eyebrow">COMMUNITY BOUNDARIES</span>
            <h2>What is never acceptable</h2>
            <ul>
              {prohibitedConduct.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>
              Harmful attempts, coded language, coordination, encouragement, and serious
              off-platform conduct connected to Topzle may also be reviewed. Do not investigate or
              confront someone yourself when that could increase risk.
            </p>
          </div>
        </section>

        <section id="rooms-calls" className="safety-policy-card">
          <CameraOff />
          <div>
            <span className="eyebrow">ROOMS, CHAT & CALLS</span>
            <h2>Share only what belongs in the room</h2>
            <ul>
              <li>Public rooms can be discoverable. Private links can still be forwarded.</li>
              <li>
                Camera and microphone require your action and device permission. Audio may start
                with first-time video so players can hear you; you can then mute them independently.
              </li>
              <li>
                Topzle does not record calls by default, but another participant can capture their
                screen or use another device. A room invitation is not permission to record.
              </li>
              <li>
                Keep names on documents, school or work details, addresses, travel plans, payment
                information, and private images out of view and chat.
              </li>
              <li>
                Hosts can manage a room, but host status does not make a person a Topzle employee or
                guarantee that they are trustworthy.
              </li>
            </ul>
          </div>
        </section>

        <section id="young-players" className="safety-policy-card">
          <UsersRound />
          <div>
            <span className="eyebrow">YOUNG PLAYERS</span>
            <h2>Extra protection for ages 13–17</h2>
            <p>
              Topzle accounts are unavailable below age 13, or a higher local minimum. Players aged
              13–17 must provide a guardian email for review. While review is pending, free-text
              chat is restricted to approved quick messages. Date of birth and guardian contact are
              not shown to other players.
            </p>
            <p>
              Never move a conversation off-platform, meet someone, send money, share intimate
              images, or disclose a home, school, live location, password, or one-time code because
              another player asks. Tell a trusted adult about pressure, secrecy, gifts, threats, or
              sexual conversation—even if the person claims to be another young player.
            </p>
          </div>
        </section>

        <section id="reporting" className="safety-policy-card">
          <ShieldCheck />
          <div>
            <span className="eyebrow">REPORTING & ENFORCEMENT</span>
            <h2>What happens after a report</h2>
            <ol>
              <li>
                <strong>Protect yourself:</strong> leave, mute, block, and contact a trusted person
                or emergency service when appropriate.
              </li>
              <li>
                <strong>Preserve useful context:</strong> note the room code, display name, time,
                game, and a factual description. Do not download, forward, or recreate illegal
                sexual material involving a child.
              </li>
              <li>
                <strong>Topzle reviews:</strong> authorised reviewers may examine the report and
                relevant account, chat, room, game, device, and moderation records.
              </li>
              <li>
                <strong>Proportionate action:</strong> outcomes can include no action, a warning,
                feature limits, removal, suspension, permanent termination, preservation, or a
                legally required referral.
              </li>
              <li>
                <strong>Appeal:</strong> contact support with the ticket or decision and explain
                what was missed. Privacy and active investigations may limit what we can disclose.
              </li>
            </ol>
            <p>
              A report is not a promise of a particular outcome. False, retaliatory, or repeated
              abusive reports can themselves lead to action.
            </p>
            <Link className="button button-primary" to="/contact">
              Contact Topzle safety
            </Link>
          </div>
        </section>

        <section id="account-safety" className="safety-policy-card">
          <KeyRound />
          <div>
            <span className="eyebrow">SCAMS & ACCOUNT SAFETY</span>
            <h2>Topzle will never ask for your one-time code in a room</h2>
            <ul>
              <li>
                Do not share sign-in links, verification codes, passwords, or recovery access.
              </li>
              <li>Do not pay a player to unlock a room, recover an account, or guarantee a win.</li>
              <li>
                Check the domain before signing in. Official web pages use topzle.com or a subdomain
                ending in .topzle.com—never a look-alike spelling.
              </li>
              <li>
                Treat urgent requests, unexpected prizes, investment offers, and requests to install
                software or share your screen as suspicious.
              </li>
              <li>
                If access may be compromised, stop active calls, secure the linked email/phone, and
                contact support with the approximate time and device.
              </li>
            </ul>
          </div>
        </section>

        <section id="families" className="safety-policy-card">
          <HeartHandshake />
          <div>
            <span className="eyebrow">PARENTS & GUARDIANS</span>
            <h2>Build a safety plan before a problem</h2>
            <ul>
              <li>Review public versus private rooms and who may receive an invitation.</li>
              <li>
                Agree when camera, microphone, chat, and country-flag visibility are appropriate.
              </li>
              <li>Keep device/browser permission and account contact information current.</li>
              <li>
                Encourage the young player to leave and tell you without fear of losing access or
                being blamed.
              </li>
              <li>
                Review the <Link to="/privacy">Privacy Notice</Link> and{" "}
                <Link to="/terms">Terms</Link>, and contact us to withdraw eligible consent or
                request account action.
              </li>
            </ul>
          </div>
        </section>
      </div>

      <section className="policy-note">
        <strong>Safety controls reduce risk; they cannot eliminate it.</strong>
        <p>
          Topzle cannot verify every identity or prevent a participant from using another device to
          record. Share the minimum information needed to play, use the controls available to you,
          and leave whenever you are uncertain. Last updated 3 August 2026.
        </p>
      </section>
    </article>
  );
}
